import React from "react"

import { OrderStatus, type SubOrder } from "@/api/api-client"

import type { VendorOrderPeriodFilter, VendorOrderStatusFilter } from "./vendor-order-types"

interface UseVendorOrderFiltersOptions {
  orders?: SubOrder[] | null
  initialStatusFilter?: VendorOrderStatusFilter
  initialPeriodFilter?: VendorOrderPeriodFilter
  initialSearchQuery?: string
}

export function useVendorOrderFilters({
  orders,
  initialStatusFilter = "pending",
  initialPeriodFilter = "30d",
  initialSearchQuery = "",
}: UseVendorOrderFiltersOptions) {
  const [statusFilter, setStatusFilter] =
    React.useState<VendorOrderStatusFilter>(initialStatusFilter)
  const [periodFilter, setPeriodFilter] =
    React.useState<VendorOrderPeriodFilter>(initialPeriodFilter)
  const [searchQuery, setSearchQuery] = React.useState(initialSearchQuery)

  const filteredOrders = React.useMemo(() => {
    if (!orders) return []

    return orders.filter((order) => {
      const status = order.status as OrderStatus | undefined

      if (
        statusFilter === "shipped" &&
        status !== OrderStatus.OrderShipped &&
        status !== OrderStatus.ReadyForPickup
      ) {
        return false
      }

      if (
        statusFilter === "pending" &&
        (status === OrderStatus.OrderComplete ||
          status === OrderStatus.OrderShipped ||
          status === OrderStatus.ReadyForPickup ||
          status === OrderStatus.OrderDeclined)
      ) {
        return false
      }

      const orderDate = order.createdAt ?? order.masterOrder?.orderDate
      if (orderDate) {
        const date = new Date(orderDate)
        const now = new Date()

        if (!Number.isNaN(date.getTime())) {
          if (periodFilter === "30d" && now.getTime() - date.getTime() > 30 * 86400000) return false
          if (periodFilter === "3m" && now.getTime() - date.getTime() > 90 * 86400000) return false
          if (periodFilter === "2026" && date.getFullYear() !== 2026) return false
          if (periodFilter === "2025" && date.getFullYear() !== 2025) return false
        }
      }

      const query = searchQuery.trim().toLowerCase()
      if (query) {
        const searchableIds = [order.id, order.masterOrder?.id, order.masterOrderId]
          .filter((value) => value != null)
          .map(String)

        if (!searchableIds.some((id) => id.toLowerCase().includes(query))) return false
      }

      return true
    })
  }, [orders, statusFilter, periodFilter, searchQuery])

  return {
    filteredOrders,
    statusFilter,
    setStatusFilter,
    periodFilter,
    setPeriodFilter,
    searchQuery,
    setSearchQuery,
  }
}
